/**
 * Live artifact previews for a feedback request, in the browser.
 *
 * The gap this closes: "pick one of these three mockups" arrived in the web
 * console as three grey letter-boxes with a first initial in them, because the
 * browser had no way to paint a shared document outside the docs route. The
 * desktop app has shown the real thing since 2026-08-18.
 *
 * Both renderers come from here because they need the same two things, and
 * both are expensive to get twice:
 *
 * - **One docs scope for the whole request.** Resolving a document id to the
 *   editor that can paint it means reading the project's document index, which
 *   needs an open collab scope. One is opened for the page, not one per option
 *   -- every artifact on a request lives in the same project.
 * - **One project resolution, and a refusal when it is ambiguous.** Exactly the
 *   rule `feedbackSubjectDocumentPath` already applies to the Open link: use
 *   the ref's project id, or the org's sole project, or decline. Guessing sends
 *   a document id into the wrong room, where it resolves to nothing and the
 *   preview looks broken rather than unavailable.
 */

import { useCallback, useMemo } from 'react';
import type { FeedbackArtifact, FeedbackRequestReadModel } from '@nimbalyst/collab-protocol';
import {
  ScaledPreviewFrame,
  type FeedbackArtifactDetailRenderer,
  type FeedbackOptionPreviewRenderer,
  type FeedbackSubjectPreviewRenderer,
} from '@nimbalyst/collab-bundle/feedback-ui';

import type { TeamSummary } from '../api';
import type { TeamJwt, TeamSession } from '../auth/teamJwt';
import { InlineCollabDocument } from '../collab/InlineCollabDocument';
import { consoleConfig } from '../config';
import { useProjectDocsScope } from '../collab/useProjectDocsScope';
import { getProjectRouteId, resolveProjectSelection } from '../projectSelection';

/**
 * Every artifact this request points at, subjects included.
 *
 * Subjects are in the list because they are published the same way and land in
 * the same project; the first resolvable ref is enough to name the scope, and a
 * request whose only document is its subject should still open one.
 */
function requestArtifacts(
  request: FeedbackRequestReadModel | null,
): readonly FeedbackArtifact[] {
  if (!request) return [];
  const subjects = (request.subjects ?? []).filter(
    (subject): subject is FeedbackArtifact => 'ref' in subject,
  );
  const bound = request.asks.flatMap((ask) => ('artifacts' in ask ? ask.artifacts ?? [] : []));
  return [...subjects, ...bound];
}

/**
 * The project every artifact on this request is read through, or null.
 *
 * Same rule as the Open link, deliberately: a ref that names its project wins,
 * a single-project org is unambiguous, and anything else declines.
 */
function resolveArtifactProject(
  team: TeamSummary,
  artifacts: readonly FeedbackArtifact[],
): TeamSummary['projects'][number] | null {
  const projects = team.projects ?? [];
  const documents = artifacts.filter((artifact) => artifact.ref.kind === 'document');
  const named = documents.find((artifact) => artifact.ref.projectId);
  if (named?.ref.projectId) {
    return resolveProjectSelection(projects, named.ref.projectId) ?? null;
  }
  if (documents.length > 0 && projects.length === 1) return projects[0]!;
  return null;
}

export interface FeedbackArtifactPreviews {
  renderOptionPreview?: FeedbackOptionPreviewRenderer;
  renderSubjectPreview?: FeedbackSubjectPreviewRenderer;
  renderArtifactDetail?: FeedbackArtifactDetailRenderer;
}

export function useFeedbackArtifactPreviews({
  team,
  teamSession,
  getTeamJwt,
  request,
}: {
  team: TeamSummary;
  teamSession: TeamSession;
  getTeamJwt: (options?: { forceRefresh?: boolean }) => Promise<TeamJwt>;
  request: FeedbackRequestReadModel | null;
}): FeedbackArtifactPreviews {
  const artifacts = useMemo(() => requestArtifacts(request), [request]);
  const project = useMemo(() => resolveArtifactProject(team, artifacts), [artifacts, team]);
  const projectId = project ? getProjectRouteId(project) : null;

  /*
   * Opened for the page rather than per card. `useProjectDocsScope` builds no
   * host at all when the project is null, so an ambiguous request costs
   * nothing -- no JWT exchange, no index socket.
   */
  const { session } = useProjectDocsScope({
    team,
    project,
    teamSession,
    getTeamJwt,
    serverUrl: consoleConfig.collabWebSocketBaseUrl,
    // Nothing here navigates: a preview is not a route, and a click inside a
    // scaled mockup cannot reach this anyway.
    navigate: () => {},
  });

  const ready = Boolean(session && projectId);

  const renderOptionPreview = useCallback<FeedbackOptionPreviewRenderer>(
    (_option, _index, artifact) => {
      if (!ready || !projectId || !artifact || artifact.ref.kind !== 'document') return undefined;
      return (
        <ScaledPreviewFrame key={artifact.ref.sourceId}>
          <InlineCollabDocument
            projectId={projectId}
            documentId={artifact.ref.sourceId}
            teamSession={teamSession}
            getTeamJwt={getTeamJwt}
          />
        </ScaledPreviewFrame>
      );
    },
    [getTeamJwt, projectId, ready, teamSession],
  );

  /*
   * A request-level subject, painted the same way an option artifact is. It
   * carries no entry id and needs none here -- the mount is addressed by the
   * document, and only the popover cares about entries.
   */
  const renderSubjectPreview = useCallback<FeedbackSubjectPreviewRenderer>(
    (subject) => {
      if (!ready || !projectId || subject.ref.kind !== 'document') return undefined;
      return (
        <ScaledPreviewFrame key={subject.ref.sourceId}>
          <InlineCollabDocument
            projectId={projectId}
            documentId={subject.ref.sourceId}
            teamSession={teamSession}
            getTeamJwt={getTeamJwt}
          />
        </ScaledPreviewFrame>
      );
    },
    [getTeamJwt, projectId, ready, teamSession],
  );

  const renderArtifactDetail = useCallback<FeedbackArtifactDetailRenderer>(
    (entry, api) => {
      const { artifact } = entry;
      if (!ready || !projectId || artifact.ref.kind !== 'document') return undefined;
      return (
        <InlineCollabDocument
          key={artifact.ref.sourceId}
          projectId={projectId}
          documentId={artifact.ref.sourceId}
          teamSession={teamSession}
          getTeamJwt={getTeamJwt}
          onViewportRegistered={api.onViewportReady}
        />
      );
    },
    [getTeamJwt, projectId, ready, teamSession],
  );

  /*
   * Withheld rather than returned-and-declining when there is no scope. An
   * absent `renderArtifactDetail` is what makes expand keep its old meaning and
   * open a tab, which is the right fallback for a request whose project cannot
   * be named -- the popover would have nothing to show.
   */
  if (!ready) return {};
  return { renderOptionPreview, renderSubjectPreview, renderArtifactDetail };
}
